/**
 * Cart Sync
 *
 * Keeps the CDN scripts' view of the cart in line with the headless cart.
 *
 * On a Liquid storefront the CDN reads the cart from Shopify's Ajax cart:
 * the `cart` cookie carries the cart token (window.analyzify.cart_id), and
 * POST /cart/update.js writes the Analyzify attribution attributes
 * (azfy_cookies, azfy_clids, azfy_utm_history, azfy_consent, azfy_cart_id)
 * onto the cart. In headless the cart lives in the Storefront API, so:
 *
 * - sync() writes the cart token into the `cart` cookie and
 *   window.analyzify.cart_id, refreshes the /cart.js shim, and lets the CDN
 *   re-run its cart upsert for a new cart;
 * - the CDN's /cart/update.js attribute writes are forwarded to the Storefront
 *   API cartAttributesUpdate mutation (merged with the cart's other
 *   attributes), so the attribution reaches the order.
 */

import type { StorefrontCart } from '../types/storefront';
import { adaptCart, createEmptyCart } from '../adapters/cart';
import { extractCartToken } from '../adapters/ids';
import { getCartState, setCartAttributeWriter, updateCartState } from '../shims/cart-api';
import { STOREFRONT_API_VERSION } from './constants';

/** Shopify's Ajax cart cookie lifetime. */
const CART_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 14;

const CART_ATTRIBUTES_QUERY = `
  query AnalyzifyCartAttributes($cartId: ID!) {
    cart(id: $cartId) {
      attributes { key value }
    }
  }
`;

const CART_ATTRIBUTES_UPDATE = `
  mutation AnalyzifyCartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart { attributes { key value } }
      userErrors { field message }
    }
  }
`;

export interface CartSyncOptions {
  shopDomain: string;
  storefrontToken?: string;
  /** Forward the CDN's cart attribute writes to the Storefront API */
  writeAttributes: boolean;
}

type AttributeInput = { key: string; value: string };

/**
 * Convert the CDN's attribute object to Storefront API AttributeInputs.
 * Strings are kept as-is, other values are JSON-serialized, null/undefined
 * values are left out (AttributeInput.value is String!).
 */
export function toAttributeInputs(attributes: Record<string, unknown>): AttributeInput[] {
  const result: AttributeInput[] = [];
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined || value === null) continue;
    result.push({ key, value: typeof value === 'string' ? value : JSON.stringify(value) });
  }
  return result;
}

/** Token part of the current `cart` cookie, if any (`<token>` or `<token>?key=<key>`). */
function cartCookieToken(): string | null {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie.split('; ').find((c) => c.startsWith('cart='));
  if (!raw) return null;
  try {
    return decodeURIComponent(raw.slice(5)).split('?')[0] || null;
  } catch {
    return null;
  }
}

/**
 * Write the cart cookie the CDN scripts read. The value keeps Shopify's full
 * `<token>?key=<key>` form: Hydrogen stores its cart ID in this same cookie
 * and needs the key; the CDN reads only the token part. An existing cookie
 * for the same cart (e.g. set by a Hydrogen server loader) is left as is.
 */
function writeCartCookie(cartId: string, token: string): void {
  if (typeof document === 'undefined') return;
  if (cartCookieToken() === token) return;
  const value = cartId.replace(/^gid:\/\/shopify\/Cart\//, '');
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `cart=${encodeURIComponent(value)}; path=/; max-age=${CART_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function clearCartCookie(token: string): void {
  if (typeof document === 'undefined') return;
  if (cartCookieToken() !== token) return; // not ours
  document.cookie = 'cart=; path=/; max-age=0';
}

/**
 * The CDN writes cart attributes only when their hash changed since the last
 * write (sessionStorage `azfy_hashes_cart`). A hash stored while there was no
 * cart yet must not block the write for the cart that appears later.
 */
function resetCdnCartAttributeHash(): void {
  try {
    sessionStorage.removeItem('azfy_hashes_cart');
  } catch {
    // Storage unavailable — the CDN then has no hash either
  }
}

/**
 * The current cart, shared by every SDK instance on the page (StrictMode,
 * provider re-mounts) — there is one cart cookie and one /cart.js shim.
 */
const current: { cartId: string | null; token: string | null } = { cartId: null, token: null };

export class CartSync {
  private options: CartSyncOptions;

  constructor(options: CartSyncOptions) {
    this.options = options;
    if (options.writeAttributes && options.storefrontToken) {
      setCartAttributeWriter((attributes, options) => this.writeAttributes(attributes, options));
    }
  }

  /**
   * Record the host app's current cart. Safe to call on every cart change.
   * Pass null when the cart no longer exists (completed checkout, cart
   * reset): the cart cookie, cart_id and the /cart.js state are cleared.
   */
  sync(cart: StorefrontCart | null | undefined): void {
    if (typeof window === 'undefined') return;
    if (!cart) {
      this.clear();
      return;
    }

    const adapted = adaptCart(cart);
    updateCartState(adapted);
    const win = window as any;
    if (win.analyzify) {
      // The CDN's in-memory cart (Liquid fills it from /cart.js on load)
      win.analyzify.detectedCart = adapted;
    }

    const token = extractCartToken(cart.id);
    if (!token) return;
    current.cartId = cart.id;
    if (token === current.token) return;
    current.token = token;

    writeCartCookie(cart.id, token);
    resetCdnCartAttributeHash();

    const analyzify = win.analyzify;
    if (!analyzify) return;
    analyzify.cart_id = token;

    // A new cart: let the CDN attach this visit's attribution to it (cart
    // upsert + attribute write), as it does when Liquid's cart token changes.
    if (analyzify.__platforms_initialized__ && typeof win.analyzify_checksendcartdata === 'function') {
      analyzify._sharedCartIdAttempts = 0;
      analyzify._updateCartAttributesCompleted = false;
      try {
        win.analyzify_checksendcartdata();
      } catch {
        // Analytics must never throw into the host app
      }
    }
  }

  /** Forget the current cart (the host's cart is gone). */
  private clear(): void {
    const win = window as any;
    if (current.token) {
      clearCartCookie(current.token);
    }
    current.cartId = null;
    current.token = null;
    const empty = createEmptyCart(getCartState().currency);
    updateCartState(empty);
    if (win.analyzify) {
      win.analyzify.cart_id = null;
      win.analyzify.detectedCart = empty;
    }
  }

  /**
   * Write attributes onto the current cart via the Storefront API, keeping the
   * cart's other attributes (cartAttributesUpdate replaces the whole set).
   * Returns the cart's attributes after the write, or null without a cart.
   *
   * The merge base is the cart's current attributes, read first; a write made
   * while the page may be unloading (`keepalive`) cannot wait for that read
   * and merges with the attributes last seen on the cart instead, as a single
   * keepalive request.
   */
  private async writeAttributes(
    attributes: Record<string, unknown>,
    { keepalive }: { keepalive: boolean }
  ): Promise<Record<string, string> | null> {
    const cartId = current.cartId;
    if (!cartId) return null;

    const merged = new Map<string, string>();
    if (keepalive) {
      for (const [key, value] of Object.entries(getCartState().attributes || {})) {
        merged.set(key, value);
      }
    } else {
      const existing = await this.request<{ cart: { attributes: AttributeInput[] } | null }>(
        CART_ATTRIBUTES_QUERY,
        { cartId }
      );
      if (!existing.cart) return null; // Cart expired or was completed
      for (const attr of existing.cart.attributes || []) {
        merged.set(attr.key, attr.value);
      }
    }
    let changed = false;
    for (const attr of toAttributeInputs(attributes)) {
      if (merged.get(attr.key) !== attr.value) changed = true;
      merged.set(attr.key, attr.value);
    }
    if (!changed) {
      // Already on the cart — nothing to write
      return Object.fromEntries(merged);
    }

    const result = await this.request<{
      cartAttributesUpdate: {
        cart: { attributes: AttributeInput[] } | null;
        userErrors: Array<{ field?: string[]; message: string }>;
      };
    }>(
      CART_ATTRIBUTES_UPDATE,
      { cartId, attributes: Array.from(merged, ([key, value]) => ({ key, value })) },
      keepalive
    );

    const payload = result.cartAttributesUpdate;
    if (payload.userErrors?.length) {
      throw new Error(payload.userErrors.map((e) => e.message).join('; '));
    }
    const out: Record<string, string> = {};
    for (const attr of payload.cart?.attributes || []) {
      out[attr.key] = attr.value;
    }
    return out;
  }

  private async request<T>(query: string, variables: Record<string, unknown>, keepalive = false): Promise<T> {
    const domain = this.options.shopDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const response = await fetch(
      `https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.options.storefrontToken || '',
        },
        body: JSON.stringify({ query, variables }),
        ...(keepalive ? { keepalive: true } : {}),
      }
    );
    if (!response.ok) {
      throw new Error(`Storefront API ${response.status}`);
    }
    const json = await response.json();
    if (json.errors?.length) {
      throw new Error(json.errors.map((e: { message: string }) => e.message).join('; '));
    }
    return json.data as T;
  }
}
